/**
 * Сортировка архива доски по столбцам.
 *
 * Архив приходит целиком, без страниц, поэтому сортируется здесь, как
 * список задач подразделения (departmentTasksSort). Пустые значения
 * всегда внизу, в какую сторону ни сортируй.
 */

import type { SortState } from "@/shared/ui";

import type { ArchivedTask } from "@taskflow/client-core/tasks";

export const ARCHIVE_SORT_FIELDS = [
  "key",
  "title",
  "type",
  "priority",
  "assignees",
  "due",
  "lastBoard",
  "lastColumn",
  "archivedAt",
  "archivedBy",
] as const;
export type ArchiveSortField = (typeof ARCHIVE_SORT_FIELDS)[number];

export function isArchiveSortField(value: string | null): value is ArchiveSortField {
  return (ARCHIVE_SORT_FIELDS as readonly string[]).includes(value ?? "");
}

const collator = new Intl.Collator("ru", { numeric: true, sensitivity: "base" });

/** Номер задачи — с учётом чисел: OA-2 раньше OA-10. */
function byKey(a: ArchivedTask, b: ArchivedTask): number {
  return collator.compare(a.key, b.key);
}

/** «—» с сервера — тоже пустая ячейка: удалённая доска или столбец. */
function text(value: string | null): string | null {
  return value === null || value === "" || value === "—" ? null : value;
}

/** Приоритет — по порядку справочника, а не по алфавиту. */
function valueOf(
  task: ArchivedTask,
  field: ArchiveSortField,
  priorityRank: ReadonlyMap<string, number>,
): string | number | null {
  switch (field) {
    case "key":
      return task.key;
    case "title":
      return task.title;
    case "type":
      return task.typeName;
    case "priority":
      return task.priorityName === null ? null : (priorityRank.get(task.priorityName) ?? null);
    case "assignees": {
      const first = task.assignees[0];
      return first === undefined ? null : `${first.lastName} ${first.firstName}`;
    }
    case "due":
      return task.dueDate;
    case "lastBoard":
      return text(task.sourceBoardName);
    case "lastColumn":
      return text(task.sourceColumnName);
    case "archivedAt":
      return task.archivedAt;
    case "archivedBy":
      return text(task.archivedByName);
  }
}

/** Без выбранного столбца — недавно архивированные сверху: архив открывают,
 *  чтобы найти то, что только что убрали. */
export function sortArchivedTasks(
  tasks: readonly ArchivedTask[],
  sort: (SortState & { field: ArchiveSortField }) | null,
  priorityRank: ReadonlyMap<string, number>,
): ArchivedTask[] {
  const field: ArchiveSortField = sort?.field ?? "archivedAt";
  const direction = sort?.direction ?? "desc";
  const sign = direction === "desc" ? -1 : 1;

  return [...tasks].sort((a, b) => {
    const left = valueOf(a, field, priorityRank);
    const right = valueOf(b, field, priorityRank);
    if (left === null || right === null) {
      if (left === right) return byKey(a, b);
      return left === null ? 1 : -1;
    }
    const order =
      typeof left === "number" && typeof right === "number"
        ? left - right
        : collator.compare(String(left), String(right));
    return order === 0 ? byKey(a, b) : sign * order;
  });
}
